import { Controller } from 'react-hook-form'
import { DatePicker, LocalizationProvider, DatePickerProps } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { Dayjs } from 'dayjs'
import strings from 'l10n'

export interface ControlledDatePickerProps extends Omit<DatePickerProps<Dayjs>, 'value' | 'onChange'> {
  /** Name of the field in the form */
  name: string
  /** React Hook Form control object */
  control: any
  /** Whether the field is required */
  required?: boolean
  /** Custom validation rules */
  rules?: any
  /** Custom required message */
  requiredMessage?: string
  /** Additional validation function that receives the current value */
  validateWhen?: (value: Dayjs | null) => boolean
}

/**
 * DatePicker component integrated with react-hook-form Controller
 *
 * This component wraps MUI's DatePicker with react-hook-form's Controller,
 * providing automatic form state management, validation, and error handling.
 *
 * @example
 * ```tsx
 * <ControlledDatePicker
 *   name="tierFixedDate"
 *   control={control}
 *   label="Fixed Date"
 *   required
 *   validateWhen={(value) => tierType === 'FIXED_DATE'}
 *   format="MMMM D, YYYY"
 * />
 * ```
 */
export const ControlledDatePicker = (props: ControlledDatePickerProps) => {
  const {
    name,
    control,
    required = false,
    rules = {},
    requiredMessage = strings.FIELD_IS_REQUIRED || 'Field is required',
    validateWhen,
    slotProps = {},
    ...datePickerProps
  } = props

  const validationRules = {
    ...rules,
    validate: {
      ...(required && {
        required: (value: Dayjs | null) => {
          // If validateWhen is provided, only validate when the condition is true
          if (validateWhen && !validateWhen(value)) {
            return true
          }
          if (!value) {
            return requiredMessage
          }
          return true
        },
      }),
      ...rules.validate,
    },
  }

  return (
    <Controller
      name={name}
      control={control}
      rules={validationRules}
      render={({ field: { onChange, value }, fieldState: { error } }) => (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            {...datePickerProps}
            value={value || null}
            onChange={onChange}
            slotProps={{
              ...slotProps,
              textField: {
                error: Boolean(error),
                helperText: error?.message,
                InputLabelProps: {
                  shrink: true,
                },
                ...slotProps.textField,
              },
            }}
          />
        </LocalizationProvider>
      )}
    />
  )
}

export default ControlledDatePicker
